import {type GridFilterModel} from '@mui/x-data-grid-pro';
import {
  isWandbArtifactRef,
  isWeaveObjectRef,
  type ObjectRef,
  parseRef,
} from '@wandb/weave/react';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {useWeaveInWorkspace} from './context/WeaveInWorkspaceContext';
import {type WFHighLevelCallFilter} from './pages/CallsPage/callsTableFilter';
import {
  type EvalTableFilter,
  ScoreFilter,
} from './pages/CompareEvaluationsPage/ecpState';
import {type WFHighLevelObjectVersionFilter} from './pages/ObjectsPage/objectsPageTypes';
import {type WFHighLevelOpVersionFilter} from './pages/OpsPage/opsPageTypes';
import {useURLSearchParamsDict} from './pages/util';
import {
  AWL_ROW_EDGE_NAME,
  DICT_KEY_EDGE_NAME,
  OBJECT_ATTR_EDGE_NAME,
} from './pages/wfReactInterface/constants';

const pruneEmptyFields = (filter: {[key: string]: any} | null | undefined) => {
  if (!filter) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(filter).filter(
      ([k, v]) =>
        v != null &&
        v !== undefined &&
        (Array.isArray(v) ? v.length > 0 : true) &&
        (typeof v === 'string' ? v.length > 0 : true)
    )
  );
};

export const useWeaveflowRouteContext = () => {
  const ctx = useContext(WeaveflowRouteContext);
  return ctx;
};

export const useWeaveflowCurrentRouteContext = () => {
  const ctx = useContext(WeaveflowRouteContext);
  const {isPeeking} = useContext(WeaveflowPeekContext);
  const {isInWorkspace} = useWeaveInWorkspace();
  if (isInWorkspace) {
    return ctx.baseRouter;
  }
  return isPeeking ? ctx.peekingRouter : ctx.baseRouter;
};

export const Browse3WeaveflowRouteContextProvider = ({
  projectRoot,
  children,
}: {
  children: ReactNode;
  projectRoot(entityName: string, projectName: string): string;
}) => {
  const baseRouter = useMemo(
    () => browse3ContextGen(projectRoot),
    [projectRoot]
  );
  const peekingRouter = useMakePeekingRouter();
  const contextValue = useMemo(
    () => ({baseRouter, peekingRouter}),
    [baseRouter, peekingRouter]
  );
  return (
    <WeaveflowRouteContext.Provider value={contextValue}>
      {children}
    </WeaveflowRouteContext.Provider>
  );
};

type WFDBTableType =
  | 'Op'
  | 'OpVersion'
  | 'Type'
  | 'TypeVersion'
  | 'Trace'
  | 'Call'
  | 'Object'
  | 'ObjectVersion';
export const browse2Context = {
  refUIUrl: (
    rootTypeName: string,
    objRef: ObjectRef,
    wfTable?: WFDBTableType
  ) => {
    if (!isWandbArtifactRef(objRef)) {
      throw new Error('Not a wandb artifact ref: ' + JSON.stringify(objRef));
    }
    return `/${objRef.entityName}/${objRef.projectName}/${rootTypeName}/${objRef.artifactName}/${objRef.artifactVersion}`;
  },
  entityUrl: (entityName: string) => {
    return `/${entityName}`;
  },
  projectUrl: (entityName: string, projectName: string) => {
    return `/${entityName}/${projectName}`;
  },
  callUIUrl: (
    entityName: string,
    projectName: string,
    traceId: string,
    callId: string,
    descendentCallId?: string,
    hideTraceTree?: boolean,
    showFeedback?: boolean,
    traceSearchQuery?: string,
    traceSearchStrict?: boolean,
    traceRootStartedAt?: string
  ) => {
    return `/${entityName}/${projectName}/trace/${traceId}/${callId}`;
  },
  typeUIUrl: (entityName: string, projectName: string, typeName: string) => {
    throw new Error('Not implemented');
  },
  objectUIUrl: (
    entityName: string,
    projectName: string,
    objectName: string
  ) => {
    throw new Error('Not implemented');
  },
  opUIUrl: (entityName: string, projectName: string, opName: string) => {
    throw new Error('Not implemented');
  },
  objectVersionUIUrl: (
    entityName: string,
    projectName: string,
    objectName: string,
    objectVersionHash: string,
    filePath?: string,
    refExtra?: string
  ) => {
    throw new Error('Not implemented');
  },
  opVersionsUIUrl: (
    entityName: string,
    projectName: string,
    filter?: WFHighLevelOpVersionFilter
  ) => {
    throw new Error('Not implemented');
  },
  opVersionUIUrl: (
    entityName: string,
    projectName: string,
    opName: string,
    opVersionHash: string
  ) => {
    return `/${entityName}/${projectName}/OpDef/${opName}/${opVersionHash}`;
  },
  evaluationsUIUrl: (entityName: string, projectName: string) => {
    throw new Error('Not implemented');
  },
  tracesUIUrl: (entityName: string, projectName: string) => {
    throw new Error('Not implemented');
  },
  callsUIUrl: (
    entityName: string,
    projectName: string,
    filter?: WFHighLevelCallFilter,
    gridFilters?: GridFilterModel
  ) => {
    throw new Error('Not implemented');
  },
  objectVersionsUIUrl: (
    entityName: string,
    projectName: string,
    filter?: WFHighLevelObjectVersionFilter
  ) => {
    throw new Error('Not implemented');
  },
  opPageUrl: (opUri: string) => {
    const parsed = parseRef(opUri);
    if (!isWandbArtifactRef(parsed)) {
      throw new Error('non wandb artifact ref not yet handled');
    }
    return browse2Context.opVersionUIUrl(
      parsed.entityName,
      parsed.projectName,
      parsed.artifactName,
      parsed.artifactVersion
    );
  },
  boardForExpressionUIUrl: (
    entityName: string,
    projectName: string,
    expression: string
  ) => {
    throw new Error('Not implemented');
  },
  compareEvaluationsUri: (
    entityName: string,
    projectName: string,
    evaluationCallIds: string[],
    metrics: Record<string, boolean> | null,
    scoreFilters?: EvalTableFilter[] | null,
    tab?: 'summary' | 'results',
    viewId?: string | null,
    inputFilters?: EvalTableFilter[] | null,
    outputFilters?: EvalTableFilter[] | null
  ) => {
    throw new Error('Not implemented');
  },
  scorerIterationUri: (
    entityName: string,
    projectName: string,
    opts: {evalName?: string | null; runCallId?: string | null}
  ) => {
    throw new Error('Not implemented');
  },
  datasetScorerUri: (): string => {
    throw new Error('Not implemented');
  },
  scorersUIUrl: (entityName: string, projectName: string) => {
    throw new Error('Not implemented');
  },
  annotationQueuesUIUrl: (
    entityName: string,
    projectName: string,
    tab?: 'queues' | 'fields'
  ) => {
    throw new Error('Not implemented');
  },
  annotationQueueDetailsUIUrl: (
    entityName: string,
    projectName: string,
    queueId: string
  ) => {
    throw new Error('Not implemented');
  },
  leaderboardsUIUrl: (
    entityName: string,
    projectName: string,
    leaderboardName?: string,
    edit?: boolean,
    type?: string
  ) => {
    throw new Error('Not implemented');
  },
  compareCallsUri: (
    entityName: string,
    projectName: string,
    callIds: string[]
  ) => {
    throw new Error('Not implemented');
  },
  compareObjectsUri: (
    entityName: string,
    projectName: string,
    objectSpecifiers: string[]
  ) => {
    throw new Error('Not implemented');
  },

  threadsUIUrl: (entityName: string, projectName: string) => {
    throw new Error('Not implemented');
  },
  threadUIUrl: (entityName: string, projectName: string, threadId: string) => {
    throw new Error('Not implemented');
  },
};

export const browse3ContextGen = (
  projectRoot: (entityName: string, projectName: string) => string
) => {
  const browse3Context = {
    refUIUrl: (
      rootTypeName: string,
      objRef: ObjectRef,
      wfTable?: WFDBTableType
    ) => {
      if (!isWandbArtifactRef(objRef) && !isWeaveObjectRef(objRef)) {
        throw new Error('Not a wandb artifact ref: ' + JSON.stringify(objRef));
      }
      if (wfTable === 'OpVersion' || rootTypeName === 'OpDef') {
        return browse3Context.opVersionUIUrl(
          objRef.entityName,
          objRef.projectName,
          objRef.artifactName,
          objRef.artifactVersion
        );
      }

      // TEMP HACK (Tim): This is a temp hack to handle old URIs logged with
      // weave client before having landed and deployed
      // https://github.com/wandb/weave/pull/1169. Should be removed before the
      // public release.
      if (isWandbArtifactRef(objRef)) {
        if (objRef.artifactPath.endsWith('rows%2F0')) {
          objRef.artifactPath = 'obj';
          let newArtifactRefExtra = `${OBJECT_ATTR_EDGE_NAME}/rows`;
          objRef.artifactRefExtra?.split('/').forEach(part => {
            if (isNaN(parseInt(part, 10))) {
              newArtifactRefExtra += `/${DICT_KEY_EDGE_NAME}/` + part;
            } else {
              newArtifactRefExtra += `/${AWL_ROW_EDGE_NAME}/` + part;
            }
          });
          objRef.artifactRefExtra = newArtifactRefExtra;
        }
      }

      if (isWandbArtifactRef(objRef)) {
        return browse3Context.objectVersionUIUrl(
          objRef.entityName,
          objRef.projectName,
          objRef.artifactName,
          objRef.artifactVersion,
          objRef.artifactPath,
          objRef.artifactRefExtra
        );
      } else if (isWeaveObjectRef(objRef)) {
        return browse3Context.objectVersionUIUrl(
          objRef.entityName,
          objRef.projectName,
          objRef.artifactName,
          objRef.artifactVersion,
          undefined,
          objRef.artifactRefExtra
        );
      }
      throw new Error('Unknown ref type');
    },
    entityUrl: (entityName: string) => {
      return `/${entityName}`;
    },
    projectUrl: (entityName: string, projectName: string) => {
      return `${projectRoot(entityName, projectName)}`;
    },
    typeUIUrl: (entityName: string, projectName: string, typeName: string) => {
      return `${projectRoot(entityName, projectName)}/types/${typeName}`;
    },
    objectUIUrl: (
      entityName: string,
      projectName: string,
      objectName: string
    ) => {
      return `${projectRoot(entityName, projectName)}/objects/${objectName}`;
    },
    opUIUrl: (entityName: string, projectName: string, opName: string) => {
      return `${projectRoot(entityName, projectName)}/ops/${opName}`;
    },
    objectVersionUIUrl: (
      entityName: string,
      projectName: string,
      objectName: string,
      objectVersionHash: string,
      filePath?: string,
      refExtra?: string
    ) => {
      const path = filePath ? `path=${encodeURIComponent(filePath)}` : '';
      const extra = refExtra ? `extra=${encodeURIComponent(refExtra)}` : '';

      return `${projectRoot(
        entityName,
        projectName
      )}/objects/${objectName}/versions/${objectVersionHash}?${path}&${extra}`;
    },
    opVersionsUIUrl: (
      entityName: string,
      projectName: string,
      filter?: WFHighLevelOpVersionFilter
    ) => {
      const prunedFilter = pruneEmptyFields(filter);
      if (Object.keys(prunedFilter).length === 0) {
        return `${projectRoot(entityName, projectName)}/op-versions`;
      }
      return `${projectRoot(
        entityName,
        projectName
      )}/op-versions?filter=${encodeURIComponent(
        JSON.stringify(prunedFilter)
      )}`;
    },
    opVersionUIUrl: (
      entityName: string,
      projectName: string,
      opName: string,
      opVersionHash: string
    ) => {
      return `${projectRoot(
        entityName,
        projectName
      )}/ops/${opName}/versions/${opVersionHash}`;
    },
    callUIUrl: (
      entityName: string,
      projectName: string,
      traceId: string,
      callId: string,
      descendentCallId?: string,
      hideTraceTree?: boolean,
      showFeedback?: boolean,
      traceSearchQuery?: string,
      traceSearchStrict?: boolean,
      traceRootStartedAt?: string,
      peekPath?: string
    ) => {
      let url = `${projectRoot(entityName, projectName)}/calls/${callId}`;
      const params = new URLSearchParams();
      if (descendentCallId) {
        params.set(DESCENDENT_CALL_ID_PARAM, descendentCallId);
      }
      if (hideTraceTree !== undefined) {
        params.set(HIDE_TRACETREE_PARAM, hideTraceTree ? '1' : '0');
      }
      if (showFeedback !== undefined) {
        params.set(SHOW_FEEDBACK_PARAM, showFeedback ? '1' : '0');
      }
      if (traceSearchQuery !== undefined && traceSearchQuery !== '') {
        params.set(TRACE_SEARCH_QUERY_PARAM, traceSearchQuery);
      }
      if (traceSearchStrict !== undefined && traceSearchStrict !== false) {
        params.set(TRACE_SEARCH_STRICT_PARAM, traceSearchStrict ? '1' : '0');
      }
      if (traceRootStartedAt) {
        params.set(TRACE_ROOT_STARTED_AT_PARAM, traceRootStartedAt);
      }
      if (peekPath) {
        params.set(PEEK_PARAM, peekPath);
      }
      if (params.toString()) {
        url += '?' + params.toString();
      }
      return url;
    },
    evaluationsUIUrl: (entityName: string, projectName: string) => {
      return `${projectRoot(entityName, projectName)}/evaluations`;
    },
    tracesUIUrl: (entityName: string, projectName: string) => {
      return `${projectRoot(entityName, projectName)}/traces`;
    },
    callsUIUrl: (
      entityName: string,
      projectName: string,
      filter?: WFHighLevelCallFilter,
      gridFilters?: GridFilterModel
    ) => {
      const searchParams = new URLSearchParams();
      const prunedFilter = pruneEmptyFields(filter);
      if (Object.keys(prunedFilter).length !== 0) {
        searchParams.set('filter', JSON.stringify(prunedFilter));
      }
      if (gridFilters) {
        searchParams.set('filters', JSON.stringify(gridFilters));
      }
      return `${projectRoot(
        entityName,
        projectName
      )}/traces?${searchParams.toString()}`;
    },
    objectVersionsUIUrl: (
      entityName: string,
      projectName: string,
      filter?: WFHighLevelObjectVersionFilter
    ) => {
      const prunedFilter = pruneEmptyFields(filter);
      if (Object.keys(prunedFilter).length === 0) {
        return `${projectRoot(entityName, projectName)}/object-versions`;
      }
      return `${projectRoot(
        entityName,
        projectName
      )}/object-versions?filter=${encodeURIComponent(
        JSON.stringify(prunedFilter)
      )}`;
    },
    opPageUrl: (opUri: string) => {
      const parsed = parseRef(opUri);
      if (!isWandbArtifactRef(parsed)) {
        throw new Error('non wandb artifact ref not yet handled');
      }
      return browse3Context.opVersionUIUrl(
        parsed.entityName,
        parsed.projectName,
        parsed.artifactName,
        parsed.artifactVersion
      );
    },
    boardForExpressionUIUrl: (
      entityName: string,
      projectName: string,
      expression: string
    ) => {
      // TODO: This is totally wrong and needs to be updated
      // when we have a proper boards page. Note, when that
      // is the case, we should not be asking the user for
      // an expression most likely.
      let base = 'https://weave.wandb.ai';
      console.log(window); // https://app.wandb.test/
      if (window.location.host === 'app.wandb.test') {
        base = 'https://weave.wandb.test';
      }
      return `${base}/?exp=${encodeURIComponent(expression)}`;
    },
    compareEvaluationsUri: (
      entityName: string,
      projectName: string,
      evaluationCallIds: string[],
      metrics: Record<string, boolean> | null,
      scoreFilters?: EvalTableFilter[] | null,
      tab?: 'summary' | 'results',
      viewId?: string | null,
      inputFilters?: EvalTableFilter[] | null,
      outputFilters?: EvalTableFilter[] | null
    ) => {
      const viewPart = viewId ? `view=${encodeURIComponent(viewId)}&` : '';
      const metricsPart = metrics
        ? `&metrics=${encodeURIComponent(JSON.stringify(metrics))}`
        : '';
      const scoreFiltersPart =
        scoreFilters && scoreFilters.length > 0
          ? `&scoreFilters=${encodeURIComponent(JSON.stringify(scoreFilters))}`
          : '';
      const inputFiltersPart =
        inputFilters && inputFilters.length > 0
          ? `&inputFilters=${encodeURIComponent(JSON.stringify(inputFilters))}`
          : '';
      const outputFiltersPart =
        outputFilters && outputFilters.length > 0
          ? `&outputFilters=${encodeURIComponent(JSON.stringify(outputFilters))}`
          : '';
      const tabPart = tab ? `&tab=${encodeURIComponent(tab)}` : '';
      return `${projectRoot(
        entityName,
        projectName
      )}/compare-evaluations?${viewPart}evaluationCallIds=${encodeURIComponent(
        JSON.stringify(evaluationCallIds)
      )}${metricsPart}${scoreFiltersPart}${inputFiltersPart}${outputFiltersPart}${tabPart}`;
    },
    scorerIterationUri: (
      entityName: string,
      projectName: string,
      opts: {evalName?: string | null; runCallId?: string | null}
    ) => {
      // Iterate-scorer is a tab inside the playground. Deep-links may carry
      // either the eval name, the run call id, or both — when only the run
      // is known, the iterate-scorer page resolves the eval name on load.
      const params = new URLSearchParams();
      if (opts.evalName) {
        params.set('eval', opts.evalName);
      }
      if (opts.runCallId) {
        params.set('run', opts.runCallId);
      }
      const qs = params.toString();
      return `${projectRoot(
        entityName,
        projectName
      )}/playground/scorer${qs ? `?${qs}` : ''}`;
    },
    datasetScorerUri: (
      entityName: string,
      projectName: string,
      opts: {
        datasetName: string;
        datasetVersion?: string | null;
        evalName?: string | null;
      }
    ) => {
      // Dataset-direct mode of the scorer playground. `?dataset` selects the
      // dataset object (latest version unless `datasetVersion` pins one);
      // `?eval` optionally seeds scorers from an existing evaluation.
      const params = new URLSearchParams();
      params.set('dataset', opts.datasetName);
      if (opts.datasetVersion) {
        params.set('datasetVersion', opts.datasetVersion);
      }
      if (opts.evalName) {
        params.set('eval', opts.evalName);
      }
      const qs = params.toString();
      return `${projectRoot(
        entityName,
        projectName
      )}/playground/scorer${qs ? `?${qs}` : ''}`;
    },
    scorersUIUrl: (entityName: string, projectName: string) => {
      return `${projectRoot(entityName, projectName)}/scorers`;
    },

    annotationQueuesUIUrl: (
      entityName: string,
      projectName: string,
      tab?: 'queues' | 'fields'
    ) => {
      const tabPart = tab ? `?tab=${tab}` : '';
      return `${projectRoot(entityName, projectName)}/annotation-queues${tabPart}`;
    },

    annotationQueueDetailsUIUrl: (
      entityName: string,
      projectName: string,
      queueId: string
    ) => {
      return `${projectRoot(entityName, projectName)}/annotation-queues/${queueId}`;
    },

    leaderboardsUIUrl: (
      entityName: string,
      projectName: string,
      leaderboardName?: string,
      edit?: boolean,
      type?: string
    ) => {
      const params = new URLSearchParams();
      if (edit) {
        params.set('edit', 'true');
      }
      if (type) {
        params.set('type', type);
      }
      const queryString = params.toString();
      return `${projectRoot(entityName, projectName)}/leaderboards${
        leaderboardName ? `/${leaderboardName}` : ''
      }${queryString ? '?' + queryString : ''}`;
    },
    compareCallsUri: (
      entityName: string,
      projectName: string,
      callIds: string[]
    ) => {
      const params = callIds
        .map(id => 'call=' + encodeURIComponent(id))
        .join('&');
      return `${projectRoot(entityName, projectName)}/compare?${params}`;
    },
    compareObjectsUri: (
      entityName: string,
      projectName: string,
      objectSpecifiers: string[]
    ) => {
      const params = objectSpecifiers
        .map(id => 'obj=' + encodeURIComponent(id))
        .join('&');
      return `${projectRoot(entityName, projectName)}/compare?${params}`;
    },
    threadsUIUrl: (entityName: string, projectName: string) => {
      return `${projectRoot(entityName, projectName)}/threads`;
    },
    threadUIUrl: (
      entityName: string,
      projectName: string,
      threadId: string
    ) => {
      return `${projectRoot(entityName, projectName)}/threads/${threadId}`;
    },
  };
  return browse3Context;
};

type RouteType = {
  refUIUrl: (
    rootTypeName: string,
    objRef: ObjectRef,
    wfTable?: WFDBTableType
  ) => string;
  entityUrl: (entityName: string) => string;
  projectUrl: (entityName: string, projectName: string) => string;
  typeUIUrl: (
    entityName: string,
    projectName: string,
    typeName: string
  ) => string;
  objectUIUrl: (
    entityName: string,
    projectName: string,
    objectName: string
  ) => string;
  opUIUrl: (entityName: string, projectName: string, opName: string) => string;
  objectVersionUIUrl: (
    entityName: string,
    projectName: string,
    objectName: string,
    objectVersionHash: string,
    filePath?: string,
    refExtra?: string
  ) => string;
  opVersionsUIUrl: (
    entityName: string,
    projectName: string,
    filter?: WFHighLevelOpVersionFilter
  ) => string;
  opVersionUIUrl: (
    entityName: string,
    projectName: string,
    opName: string,
    opVersionHash: string
  ) => string;
  callUIUrl: (
    entityName: string,
    projectName: string,
    traceId: string,
    callId: string,
    descendentCallId?: string,
    hideTraceTree?: boolean,
    showFeedback?: boolean,
    traceSearchQuery?: string,
    traceSearchStrict?: boolean,
    traceRootStartedAt?: string,
    peekPath?: string
  ) => string;
  evaluationsUIUrl: (entityName: string, projectName: string) => string;
  tracesUIUrl: (entityName: string, projectName: string) => string;
  callsUIUrl: (
    entityName: string,
    projectName: string,
    filter?: WFHighLevelCallFilter,
    // Using GridFilterModel here is really bad. Somehow this leaked into
    // the implementation and now it is a part of our URL spec forever... :(
    // It should have been implemented as the `query` component of WFHighLevelCallFilter
    // which maps to our actual service API.
    gridFilters?: GridFilterModel
  ) => string;
  objectVersionsUIUrl: (
    entityName: string,
    projectName: string,
    filter?: WFHighLevelObjectVersionFilter
  ) => string;
  boardForExpressionUIUrl: (
    entityName: string,
    projectName: string,
    expression: string
    // TODO: Add filter when supported
  ) => string;
  opPageUrl: (opUri: string) => string;
  compareEvaluationsUri: (
    entityName: string,
    projectName: string,
    evaluationCallIds: string[],
    metrics: Record<string, boolean> | null,
    scoreFilters?: EvalTableFilter[] | null,
    tab?: 'summary' | 'results',
    viewId?: string | null,
    inputFilters?: EvalTableFilter[] | null,
    outputFilters?: EvalTableFilter[] | null
  ) => string;
  scorerIterationUri: (
    entityName: string,
    projectName: string,
    opts: {evalName?: string | null; runCallId?: string | null}
  ) => string;
  datasetScorerUri: (
    entityName: string,
    projectName: string,
    opts: {
      datasetName: string;
      datasetVersion?: string | null;
      evalName?: string | null;
    }
  ) => string;
  scorersUIUrl: (entityName: string, projectName: string) => string;

  annotationQueuesUIUrl: (
    entityName: string,
    projectName: string,
    tab?: 'queues' | 'fields'
  ) => string;

  annotationQueueDetailsUIUrl: (
    entityName: string,
    projectName: string,
    queueId: string
  ) => string;

  leaderboardsUIUrl: (
    entityName: string,
    projectName: string,
    leaderboardName?: string,
    edit?: boolean,
    type?: string
  ) => string;
  compareCallsUri: (
    entityName: string,
    projectName: string,
    callIds: string[]
  ) => string;
  compareObjectsUri: (
    entityName: string,
    projectName: string,
    objectSpecifiers: string[]
  ) => string;
  threadsUIUrl: (entityName: string, projectName: string) => string;
  threadUIUrl: (
    entityName: string,
    projectName: string,
    threadId: string
  ) => string;
};

const useSetSearchParam = () => {
  const location = useLocation();
  return useCallback(
    (key: string, value: string | null) => {
      const searchParams = new URLSearchParams(location.search);
      if (value === null) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}?${newSearch}`;
      return newUrl;
    },
    [location]
  );
};

export const PEEK_PARAM = 'peekPath';
export const HIDE_TRACETREE_PARAM = 'hideTraceTree';
export const SHOW_FEEDBACK_PARAM = 'showFeedback';
export const DESCENDENT_CALL_ID_PARAM = 'descendentCallId';
/** URL parameter for the trace filter search query and mode */
export const TRACE_SEARCH_QUERY_PARAM = 'traceSearchQuery';
export const TRACE_SEARCH_STRICT_PARAM = 'traceSearchStrict';
/**
 * Optional URL parameter carrying the trace root call's `started_at` so the
 * trace fetch can constrain `sortable_datetime` on the server and avoid a full
 * project scan. Callers that already have this value (table rows, trace tree
 * navigation, charts) should pass it through; anything that omits it falls
 * back to the prior behavior of only bounding when the focused call is the
 * root. Carrying the param across navigation is internal to the URL router
 * and to `CallLink` — direct call sites do not need to thread it manually.
 */
export const TRACE_ROOT_STARTED_AT_PARAM = 'traceRootStartedAt';

export const baseContext = browse3ContextGen(
  (entityName: string, projectName: string) => {
    return `/${entityName}/${projectName}`;
  }
);

const useMakePeekingRouter = (): RouteType => {
  const setSearchParam = useSetSearchParam();

  return useMemo(
    () => ({
      refUIUrl: (...args: Parameters<typeof baseContext.refUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.refUIUrl(...args));
      },
      entityUrl: (...args: Parameters<typeof baseContext.entityUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.entityUrl(...args));
      },
      projectUrl: (...args: Parameters<typeof baseContext.projectUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.projectUrl(...args));
      },
      typeUIUrl: (...args: Parameters<typeof baseContext.typeUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.typeUIUrl(...args));
      },
      objectUIUrl: (...args: Parameters<typeof baseContext.objectUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.objectUIUrl(...args));
      },
      opUIUrl: (...args: Parameters<typeof baseContext.opUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.opUIUrl(...args));
      },
      objectVersionUIUrl: (
        ...args: Parameters<typeof baseContext.objectVersionUIUrl>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.objectVersionUIUrl(...args)
        );
      },
      opVersionsUIUrl: (
        ...args: Parameters<typeof baseContext.opVersionsUIUrl>
      ) => {
        return setSearchParam(PEEK_PARAM, baseContext.opVersionsUIUrl(...args));
      },
      opVersionUIUrl: (
        ...args: Parameters<typeof baseContext.opVersionUIUrl>
      ) => {
        return setSearchParam(PEEK_PARAM, baseContext.opVersionUIUrl(...args));
      },
      callUIUrl: (...args: Parameters<typeof baseContext.callUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.callUIUrl(...args));
      },
      evaluationsUIUrl: (
        ...args: Parameters<typeof baseContext.evaluationsUIUrl>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.evaluationsUIUrl(...args)
        );
      },
      tracesUIUrl: (...args: Parameters<typeof baseContext.tracesUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.tracesUIUrl(...args));
      },
      callsUIUrl: (...args: Parameters<typeof baseContext.callsUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.callsUIUrl(...args));
      },
      objectVersionsUIUrl: (
        ...args: Parameters<typeof baseContext.objectVersionsUIUrl>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.objectVersionsUIUrl(...args)
        );
      },
      opPageUrl: (...args: Parameters<typeof baseContext.opPageUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.opPageUrl(...args));
      },
      boardForExpressionUIUrl: (
        ..._args: Parameters<typeof baseContext.boardForExpressionUIUrl>
      ) => {
        throw new Error('Not implemented');
        // return setSearchParam(
        //   PEEK_PARAM,
        //   baseContext.boardForExpressionUIUrl(...args)
        // );
      },
      compareEvaluationsUri: (
        ...args: Parameters<typeof baseContext.compareEvaluationsUri>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.compareEvaluationsUri(...args)
        );
      },
      scorerIterationUri: (
        ...args: Parameters<typeof baseContext.scorerIterationUri>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.scorerIterationUri(...args)
        );
      },
      datasetScorerUri: (
        ...args: Parameters<typeof baseContext.datasetScorerUri>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.datasetScorerUri(...args)
        );
      },
      scorersUIUrl: (...args: Parameters<typeof baseContext.scorersUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.scorersUIUrl(...args));
      },
      annotationQueuesUIUrl: (
        ...args: Parameters<typeof baseContext.annotationQueuesUIUrl>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.annotationQueuesUIUrl(...args)
        );
      },
      annotationQueueDetailsUIUrl: (
        ...args: Parameters<typeof baseContext.annotationQueueDetailsUIUrl>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.annotationQueueDetailsUIUrl(...args)
        );
      },
      leaderboardsUIUrl: (
        ...args: Parameters<typeof baseContext.leaderboardsUIUrl>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.leaderboardsUIUrl(...args)
        );
      },
      compareCallsUri: (
        ...args: Parameters<typeof baseContext.compareCallsUri>
      ) => {
        return setSearchParam(PEEK_PARAM, baseContext.compareCallsUri(...args));
      },
      compareObjectsUri: (
        ...args: Parameters<typeof baseContext.compareObjectsUri>
      ) => {
        return setSearchParam(
          PEEK_PARAM,
          baseContext.compareObjectsUri(...args)
        );
      },
      threadsUIUrl: (..._args: Parameters<typeof baseContext.threadsUIUrl>) => {
        throw new Error('Threads list not available in peek mode');
      },
      threadUIUrl: (...args: Parameters<typeof baseContext.threadUIUrl>) => {
        return setSearchParam(PEEK_PARAM, baseContext.threadUIUrl(...args));
      },
    }),
    [setSearchParam]
  );
};

const WeaveflowRouteContext = createContext<{
  baseRouter: RouteType;
  peekingRouter: RouteType;
}>({
  baseRouter: browse2Context,
  peekingRouter: browse2Context,
});

export const WeaveflowPeekContext = createContext<{
  isPeeking?: boolean;
  peekPreviousUrl?: string;
  setPeekPreviousUrl?: (url: string) => void;
}>({
  isPeeking: false,
});

export const NavigationHistoryContext = createContext<{
  previousUrl?: string;
  setPreviousUrl?: (url: string) => void;
}>({});

/**
 * Hook for compare evaluations back navigation
 * Returns the previous URL if one was saved, otherwise returns undefined
 */
export const useBackNavigation = () => {
  const history = useHistory();
  const {previousUrl} = useContext(NavigationHistoryContext);

  return useCallback(
    (fallbackUrl: string) => {
      history.push(previousUrl ?? fallbackUrl);
    },
    [history, previousUrl]
  );
};

export const useClosePeek = () => {
  const history = useHistory();
  return () => {
    const queryParams = new URLSearchParams(history.location.search);
    if (queryParams.has(PEEK_PARAM)) {
      queryParams.delete(PEEK_PARAM);
      history.replace({
        search: queryParams.toString(),
      });
    }
  };
};

/**
 * Hook for peek drawer navigation (e.g., navigating between traces in CallPage pagination)
 * This is separate from the compare evaluations back navigation
 */
export const usePeekBackNavigation = () => {
  const history = useHistory();
  const {peekPreviousUrl} = useContext(WeaveflowPeekContext);

  return useCallback(() => {
    if (peekPreviousUrl) {
      history.push(peekPreviousUrl);
    } else {
      // Fallback to closing peek if no previous URL
      const queryParams = new URLSearchParams(history.location.search);
      if (queryParams.has(PEEK_PARAM)) {
        queryParams.delete(PEEK_PARAM);
        history.replace({
          search: queryParams.toString(),
        });
      }
    }
  }, [history, peekPreviousUrl]);
};

export const useSaveCurrentUrl = () => {
  const {setPreviousUrl} = useContext(NavigationHistoryContext);

  return useCallback(() => {
    if (setPreviousUrl) {
      const currentUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      setPreviousUrl(currentUrl);
    }
  }, [setPreviousUrl]);
};

export const usePeekLocation = () => {
  const {peekPath} = useURLSearchParamsDict();

  return useMemo(() => {
    if (peekPath == null) {
      return undefined;
    }
    const peekPathParts = peekPath.split('?');
    const peekPathname = peekPathParts[0];
    const peekSearch = peekPathParts[1] ?? '';
    const peekSearchParts = peekSearch.split('#');
    const peekSearchString = peekSearchParts[0];
    const peekHash = peekSearchParts[1] ?? '';

    return {
      key: 'peekLoc',
      pathname: peekPathname,
      search: peekSearchString,
      hash: peekHash,
      state: {
        '[userDefined]': true,
      },
    };
  }, [peekPath]);
};

export const WeaveHeaderExtrasContext = createContext<{
  extras: {[key: string]: HeaderExtra};
  addExtra: (key: string, value: HeaderExtra) => void;
  removeExtra: (key: string) => void;
  renderExtras: () => ReactNode;
}>({
  extras: {},
  addExtra: () => {},
  removeExtra: () => {},
  renderExtras: () => null,
});

type HeaderExtra = {node: ReactNode; order?: number};

export const WeaveHeaderExtrasProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [extras, setExtras] = useState<{[key: string]: HeaderExtra}>({});
  const addExtra = useCallback(
    (key: string, extra: HeaderExtra) => {
      setExtras(prev => {
        return {...prev, [key]: extra};
      });
    },
    [setExtras]
  );

  const removeExtra = useCallback(
    (key: string) => {
      setExtras(prev => {
        const newExtras = {...prev};
        delete newExtras[key];
        return newExtras;
      });
    },
    [setExtras]
  );

  const renderExtras = useCallback(() => {
    return (
      <>
        {Object.entries(extras)
          .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
          .map(([key, extra]) => {
            return <React.Fragment key={key}>{extra.node}</React.Fragment>;
          })}
      </>
    );
  }, [extras]);

  const value = useMemo(
    () => ({extras, addExtra, removeExtra, renderExtras}),
    [extras, addExtra, removeExtra, renderExtras]
  );

  return (
    <WeaveHeaderExtrasContext.Provider value={value}>
      {children}
    </WeaveHeaderExtrasContext.Provider>
  );
};

// Context for run colors - optional, will be null if not provided
export const RunColorsContext = React.createContext<{
  getRunColor: (runId: string) => string | null;
} | null>(null);

export const EntityProjectContext = React.createContext<{
  entity: string;
  project: string;
  projectId: string;
}>({
  entity: '',
  project: '',
  projectId: '',
});

export const EntityProjectProvider: React.FC<{
  children: React.ReactNode;
  entity: string;
  project: string;
}> = ({children, entity, project}) => {
  const value = useMemo(
    () => ({
      entity,
      project,
      projectId: `${entity}/${project}`,
    }),
    [entity, project]
  );
  return (
    <EntityProjectContext.Provider value={value}>
      {children}
    </EntityProjectContext.Provider>
  );
};

export const useEntityProject = () => {
  return useContext(EntityProjectContext);
};

// TODO: Refactor this out when useOrgRampFlagWithLoading can be moved to the weave side for consumption
export type WeaveFeatureFlags = {
  weave_debounced_scoring: boolean;
  weave_remote_scoring: boolean;
  weave_trace_analysis_frontend: boolean;
  hide_weave_monitors_tab: boolean;
};

export const DEFAULT_WEAVE_FEATURE_FLAGS: WeaveFeatureFlags = {
  weave_debounced_scoring: false,
  weave_remote_scoring: false,
  weave_trace_analysis_frontend: false,
  hide_weave_monitors_tab: false,
};

export const WeaveFeatureFlagsContext = React.createContext<WeaveFeatureFlags>(
  DEFAULT_WEAVE_FEATURE_FLAGS
);

export const WeaveFeatureFlagsProvider: React.FC<{
  children: React.ReactNode;
  value: WeaveFeatureFlags;
}> = ({children, value}) => {
  return (
    <WeaveFeatureFlagsContext.Provider value={value}>
      {children}
    </WeaveFeatureFlagsContext.Provider>
  );
};
